import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import {
  Box,
  Container,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Typography,
  Card,
  CardContent,
  Grid,
  FormControlLabel,
  Checkbox,
} from '@material-ui/core';
import { Edit as EditIcon, Eye as EyeIcon, Plus as PlusIcon, DollarSign as PriceIcon } from 'react-feather';
import { api } from 'src/service';

const Fleets = () => {
  const [fleets, setFleets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openMembers, setOpenMembers] = useState(false);
  const [members, setMembers] = useState([]);
  const [selectedFleet, setSelectedFleet] = useState(null);
  const [form, setForm] = useState({
    company_vat: '',
    company_name: '',
    discount_percent: '',
    post_pay: false,
    email: '',
  });
  const [error, setError] = useState('');
  const [viesState, setViesState] = useState({ checking: false, locked: false, checked: false });

  const resetForm = () => {
    setForm({ company_vat: '', company_name: '', discount_percent: '', post_pay: false, email: '' });
    setViesState({ checking: false, locked: false, checked: false });
  };

  const checkVies = async (vat) => {
    const cleaned = String(vat || '').replace(/\D/g, '');
    if (cleaned.length !== 9) {
      setViesState({ checking: false, locked: false, checked: false });
      return;
    }
    setViesState({ checking: true, locked: false, checked: false });
    try {
      const res = await api.get(`/backoffice/fleets/vies/${cleaned}`);
      const { valid, name, source } = res.data.data || {};
      if (valid && name) {
        setForm((f) => ({ ...f, company_name: name }));
        setViesState({ checking: false, locked: true, checked: true });
      } else {
        // Not found in VIES (or local-only fallback) — unlock for manual entry
        setForm((f) => ({ ...f, company_name: '' }));
        setViesState({ checking: false, locked: false, checked: true });
      }
    } catch (err) {
      setViesState({ checking: false, locked: false, checked: true });
    }
  };

  const fetchFleets = async () => {
    setLoading(true);
    try {
      const res = await api.get('/backoffice/fleets');
      setFleets(res.data.data);
    } catch (err) {
      console.error('Error fetching fleets:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchFleets();
  }, []);

  const handleCreate = async () => {
    setError('');
    try {
      await api.post('/backoffice/fleets', {
        company_vat: form.company_vat.trim(),
        company_name: form.company_name.trim(),
        discount_percent: parseInt(form.discount_percent) || 0,
        post_pay: !!form.post_pay,
        email: form.email ? form.email.trim() : null,
      });
      setOpenCreate(false);
      resetForm();
      fetchFleets();
    } catch (err) {
      setError(err.response?.data?.moreInformation?.error || 'Error creating fleet');
    }
  };

  const handleUpdate = async () => {
    setError('');
    try {
      const updates = {};
      if (form.company_name) updates.company_name = form.company_name.trim();
      if (form.discount_percent !== '') updates.discount_percent = parseInt(form.discount_percent);
      if (form.post_pay !== undefined) updates.post_pay = !!form.post_pay;
      if (form.status !== undefined) updates.status = form.status;
      if (form.email !== undefined) updates.email = form.email ? form.email.trim() : null;

      await api.put(`/backoffice/fleets/${selectedFleet.id}`, updates);
      setOpenEdit(false);
      fetchFleets();
    } catch (err) {
      setError(err.response?.data?.moreInformation?.error || 'Error updating fleet');
    }
  };

  const handleToggleStatus = async (fleet) => {
    try {
      const newStatus = fleet.status === 'active' ? 'inactive' : 'active';
      await api.put(`/backoffice/fleets/${fleet.id}`, { status: newStatus });
      fetchFleets();
    } catch (err) {
      alert(err.response?.data?.moreInformation?.error || 'Error toggling status');
    }
  };

  const handleViewMembers = async (fleet) => {
    setSelectedFleet(fleet);
    try {
      const res = await api.get(`/backoffice/fleets/${fleet.id}/members`);
      setMembers(res.data.data.members);
      setOpenMembers(true);
    } catch (err) {
      console.error('Error fetching members:', err);
    }
  };

  const openEditDialog = (fleet) => {
    setSelectedFleet(fleet);
    setForm({
      company_vat: fleet.company_vat,
      company_name: fleet.company_name,
      discount_percent: String(fleet.discount_percent),
      post_pay: !!fleet.post_pay,
      email: fleet.email || '',
      status: fleet.status,
    });
    // Editing existing fleet — VAT is read-only; treat company name as
    // confirmed-from-VIES (locked) unless the admin chooses to override.
    setViesState({ checking: false, locked: true, checked: true });
    setError('');
    setOpenEdit(true);
  };

  // ── Per-fleet pricing ───────────────────────────────────────────
  const [openPrices, setOpenPrices] = useState(false);
  const [priceServices, setPriceServices] = useState([]);
  const [priceOverrides, setPriceOverrides] = useState({}); // { service_id: { 'Class 1': price, 'Class 2': price } }
  const [priceFleet, setPriceFleet] = useState(null);
  const [priceSaving, setPriceSaving] = useState(false);

  const openPricesDialog = async (fleet) => {
    setPriceFleet(fleet);
    setError('');
    setOpenPrices(true);
    try {
      const res = await api.get(`/backoffice/fleets/${fleet.id}/prices`);
      const { services, overrides } = res.data.data;
      setPriceServices(services || []);
      const map = {};
      for (const o of (overrides || [])) {
        if (!map[o.service_id]) map[o.service_id] = {};
        map[o.service_id][o.vehicle_class] = String(o.price);
      }
      setPriceOverrides(map);
    } catch (err) {
      console.error('Error loading fleet prices:', err);
      setError('Failed to load prices');
    }
  };

  const setOverrideCell = (serviceId, vClass, value) => {
    setPriceOverrides((prev) => {
      const next = { ...prev, [serviceId]: { ...(prev[serviceId] || {}) } };
      next[serviceId][vClass] = value;
      return next;
    });
  };

  const savePriceCell = async (serviceId, vClass) => {
    if (!priceFleet) return;
    setPriceSaving(true);
    try {
      const raw = (priceOverrides[serviceId] || {})[vClass];
      const body = { service_id: serviceId, vehicle_class: vClass, price: raw === '' || raw === undefined ? null : raw };
      await api.put(`/backoffice/fleets/${priceFleet.id}/prices`, body);
    } catch (err) {
      setError(err.response?.data?.moreInformation?.error || 'Error saving price');
    }
    setPriceSaving(false);
  };

  const clearPriceCell = async (serviceId, vClass) => {
    if (!priceFleet) return;
    setPriceSaving(true);
    try {
      await api.delete(`/backoffice/fleets/${priceFleet.id}/prices/${serviceId}/${encodeURIComponent(vClass)}`);
      setOverrideCell(serviceId, vClass, '');
    } catch (err) {
      setError(err.response?.data?.moreInformation?.error || 'Error clearing price');
    }
    setPriceSaving(false);
  };

  return (
    <>
      <Helmet>
        <title>Washer - Fleet Management</title>
      </Helmet>
      <Box sx={{ backgroundColor: 'background.default', minHeight: '100%', py: 3 }}>
        <Container maxWidth={false}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h4">Fleet Management</Typography>
            <Button
              color="primary"
              variant="contained"
              onClick={() => {
                resetForm();
                setError('');
                setOpenCreate(true);
              }}
              startIcon={<PlusIcon />}
            >
              New Fleet
            </Button>
          </Box>

          <Card>
            <CardContent>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Company VAT</TableCell>
                      <TableCell>Company Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Discount %</TableCell>
                      <TableCell>Post-pay</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Members</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fleets.map((fleet) => (
                      <TableRow key={fleet.id}>
                        <TableCell><strong>{fleet.company_vat}</strong></TableCell>
                        <TableCell>{fleet.company_name}</TableCell>
                        <TableCell>{fleet.email || '—'}</TableCell>
                        <TableCell>{fleet.discount_percent}%</TableCell>
                        <TableCell>
                          {fleet.post_pay
                            ? <Chip label="Post-pay" color="primary" size="small" />
                            : <Chip label="No" size="small" />}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={fleet.status}
                            color={fleet.status === 'active' ? 'primary' : 'default'}
                            size="small"
                            onClick={() => handleToggleStatus(fleet)}
                            style={{ cursor: 'pointer' }}
                          />
                        </TableCell>
                        <TableCell>{fleet.member_count}</TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => openEditDialog(fleet)} title="Edit">
                            <EditIcon size={16} />
                          </IconButton>
                          <IconButton size="small" onClick={() => openPricesDialog(fleet)} title="Custom Prices">
                            <PriceIcon size={16} />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleViewMembers(fleet)} title="View Members">
                            <EyeIcon size={16} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    {fleets.length === 0 && !loading && (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          No fleets yet
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Container>
      </Box>

      {/* Create Dialog */}
      <Dialog open={openCreate} onClose={() => setOpenCreate(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Fleet</DialogTitle>
        <DialogContent>
          {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField
                label="Company VAT / NIF"
                value={form.company_vat}
                onChange={(e) => {
                  const v = e.target.value;
                  setForm({ ...form, company_vat: v, company_name: '' });
                  setViesState({ checking: false, locked: false, checked: false });
                }}
                onBlur={() => checkVies(form.company_vat)}
                fullWidth
                placeholder="e.g. 501234567"
                helperText={
                  viesState.checking ? 'Checking VIES…' :
                  (viesState.checked && viesState.locked) ? 'VIES match — name locked' :
                  (viesState.checked && !viesState.locked) ? 'Not found in VIES — enter company name manually' :
                  'Enter VAT then tab away to look up the name in VIES'
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Company Name"
                value={form.company_name}
                onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                fullWidth
                disabled={viesState.checking || viesState.locked || !viesState.checked}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Discount %"
                type="number"
                value={form.discount_percent}
                onChange={(e) => setForm({ ...form, discount_percent: e.target.value })}
                fullWidth
                inputProps={{ min: -100, max: 100 }}
                helperText="Negative = surcharge (price increase)"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Email (optional)"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                fullWidth
                placeholder="contact@company.pt"
                helperText="Used by future automations (invoicing, statements)"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!form.post_pay}
                    onChange={(e) => setForm({ ...form, post_pay: e.target.checked })}
                  />
                }
                label="Post-payment (members with company profile active skip payment and are invoiced later)"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreate(false)}>Cancel</Button>
          <Button onClick={handleCreate} color="primary" variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={openEdit} onClose={() => setOpenEdit(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Fleet: {selectedFleet?.company_name}</DialogTitle>
        <DialogContent>
          {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField
                label="Company Name"
                value={form.company_name}
                onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Discount %"
                type="number"
                value={form.discount_percent}
                onChange={(e) => setForm({ ...form, discount_percent: e.target.value })}
                fullWidth
                inputProps={{ min: -100, max: 100 }}
                helperText="Negative = surcharge (price increase)"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Email"
                type="email"
                value={form.email || ''}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                fullWidth
                placeholder="contact@company.pt"
                helperText="Used by future automations (invoicing, statements)"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!form.post_pay}
                    onChange={(e) => setForm({ ...form, post_pay: e.target.checked })}
                  />
                }
                label="Post-payment (members with company profile active skip payment and are invoiced later)"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEdit(false)}>Cancel</Button>
          <Button onClick={handleUpdate} color="primary" variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Members Dialog */}
      <Dialog open={openMembers} onClose={() => setOpenMembers(false)} maxWidth="md" fullWidth>
        <DialogTitle>Fleet Members: {selectedFleet?.company_name}</DialogTitle>
        <DialogContent>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Personal NIF</TableCell>
                  <TableCell>Active Profile</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {members.map((m) => (
                  <TableRow key={m.id}>
                    <TableCell>{m.firstname} {m.lastname}</TableCell>
                    <TableCell>{m.email}</TableCell>
                    <TableCell>{m.phonenumber}</TableCell>
                    <TableCell>{m.nif || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={m.active_profile || 'personal'}
                        color={m.active_profile === 'company' ? 'primary' : 'default'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {members.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">No members yet</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenMembers(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Custom Prices Dialog */}
      <Dialog open={openPrices} onClose={() => setOpenPrices(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Custom Prices: {priceFleet?.company_name}
          <Typography variant="caption" display="block" color="textSecondary">
            Leave blank to use the standard service price. Custom prices override the fleet % discount (promo codes still apply).
          </Typography>
        </DialogTitle>
        <DialogContent>
          {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Service</TableCell>
                  <TableCell align="right">Standard Class 1</TableCell>
                  <TableCell align="center">Fleet Class 1 (€)</TableCell>
                  <TableCell align="right">Standard Class 2</TableCell>
                  <TableCell align="center">Fleet Class 2 (€)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {priceServices.map((svc) => (
                  <TableRow key={svc.id}>
                    <TableCell><strong>{svc.service_name}</strong></TableCell>
                    <TableCell align="right">{Number(svc.price_class_1).toFixed(2)} €</TableCell>
                    <TableCell align="center">
                      <TextField
                        type="number"
                        size="small"
                        value={(priceOverrides[svc.id] || {})['Class 1'] || ''}
                        onChange={(e) => setOverrideCell(svc.id, 'Class 1', e.target.value)}
                        onBlur={() => savePriceCell(svc.id, 'Class 1')}
                        inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right', width: 80 } }}
                        placeholder="—"
                        disabled={priceSaving}
                      />
                      {((priceOverrides[svc.id] || {})['Class 1']) && (
                        <Button size="small" onClick={() => clearPriceCell(svc.id, 'Class 1')} disabled={priceSaving}>×</Button>
                      )}
                    </TableCell>
                    <TableCell align="right">{Number(svc.price_class_2).toFixed(2)} €</TableCell>
                    <TableCell align="center">
                      <TextField
                        type="number"
                        size="small"
                        value={(priceOverrides[svc.id] || {})['Class 2'] || ''}
                        onChange={(e) => setOverrideCell(svc.id, 'Class 2', e.target.value)}
                        onBlur={() => savePriceCell(svc.id, 'Class 2')}
                        inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right', width: 80 } }}
                        placeholder="—"
                        disabled={priceSaving}
                      />
                      {((priceOverrides[svc.id] || {})['Class 2']) && (
                        <Button size="small" onClick={() => clearPriceCell(svc.id, 'Class 2')} disabled={priceSaving}>×</Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {priceServices.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">Loading services…</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPrices(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Fleets;
